<script setup>
// Sales presentation of project paperwork — visually matches the reference
// design at docs/addon/documents.png. Reuses the existing project documents
// endpoint verbatim (GET /nexceo/projects/:id/documents) and the existing
// customer_shareable flag to split Customer Ready vs. Internal — no new
// visibility system. Photos/videos/immersive links keep their own
// dedicated tabs (Media/Immersive) so they aren't duplicated here — the
// reference image bundles everything into one page, but this app already
// has a deliberate Documents/Media/Immersive split from the original spec,
// so this applies the reference's visual language to Documents' own
// content rather than merging the tabs.
//
// Real thumbnails: 'image'-type docs would use their own shareable_url
// (not applicable here — excluded like the other media types), everything
// else uses the optional admin-uploaded Nexceo::ProjectDocument#thumbnail
// (see project_document.rb) and falls back to a type-colored icon tile
// when absent. The "Share Project Kit" panel has no WhatsApp/Email backend
// send calls — the one real send pipeline (Nexceo::DocumentShareService) is
// conversation/lead-bound AI-adjacent code this feature must not touch.
// Uses plain client-side wa.me/mailto deep links and a clipboard copy
// instead. "Recently Shared" IS real, logged via Nexceo::ProjectDocumentShare
// right after one of those client-side actions succeeds — it never records
// who received the link (that's genuinely not tracked by a wa.me/mailto
// deep link), only who shared what, via which channel, when.
import { ref, computed, onMounted, onUnmounted } from 'vue';
import projectDocumentsAPI from 'dashboard/api/nexceo/projectDocuments';
import projectDocumentSharesAPI from 'dashboard/api/nexceo/projectDocumentShares';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

const props = defineProps({
  project: { type: Object, default: null },
  projectId: { type: Number, required: true },
});

const MEDIA_TYPES = [
  'image',
  'video',
  'walkthrough_video',
  'immersive',
  'immersive_sample_flat',
];

const DOC_TYPE_META = {
  brochure: {
    label: 'Brochure',
    icon: 'i-lucide-book-open',
    tile: 'bg-woot-50 dark:bg-woot-500/10',
    badge: 'bg-woot-50 text-woot-700 dark:bg-woot-500/20 dark:text-woot-400',
    iconColor: 'text-woot-600 dark:text-woot-400',
  },
  floor_plan: {
    label: 'Floor Plan',
    icon: 'i-lucide-layout-grid',
    tile: 'bg-green-50 dark:bg-green-500/10',
    badge:
      'bg-green-50 text-green-700 dark:bg-green-500/20 dark:text-green-400',
    iconColor: 'text-green-600 dark:text-green-400',
  },
  location_map: {
    label: 'Master Plan',
    icon: 'i-lucide-map',
    tile: 'bg-violet-50 dark:bg-violet-500/10',
    badge:
      'bg-violet-50 text-violet-700 dark:bg-violet-500/20 dark:text-violet-400',
    iconColor: 'text-violet-600 dark:text-violet-400',
  },
  amenities: {
    label: 'Amenities',
    icon: 'i-lucide-sparkles',
    tile: 'bg-yellow-50 dark:bg-yellow-500/10',
    badge:
      'bg-yellow-50 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
  },
  faq: {
    label: 'FAQ',
    icon: 'i-lucide-help-circle',
    tile: 'bg-n-alpha-2',
    badge: 'bg-n-alpha-2 text-n-slate-10',
    iconColor: 'text-n-slate-9',
  },
  price_sheet: {
    label: 'Price Sheet',
    icon: 'i-lucide-indian-rupee',
    tile: 'bg-red-50 dark:bg-red-500/10',
    badge: 'bg-red-50 text-red-700 dark:bg-red-500/20 dark:text-red-400',
    iconColor: 'text-red-600 dark:text-red-400',
  },
  payment_plan: {
    label: 'Payment Plan',
    icon: 'i-lucide-calendar-clock',
    tile: 'bg-red-50 dark:bg-red-500/10',
    badge: 'bg-red-50 text-red-700 dark:bg-red-500/20 dark:text-red-400',
    iconColor: 'text-red-600 dark:text-red-400',
  },
  rera: {
    label: 'RERA',
    icon: 'i-lucide-shield-check',
    tile: 'bg-yellow-50 dark:bg-yellow-500/10',
    badge:
      'bg-yellow-50 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
  },
  legal: {
    label: 'Legal',
    icon: 'i-lucide-scale',
    tile: 'bg-yellow-50 dark:bg-yellow-500/10',
    badge:
      'bg-yellow-50 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
  },
  approval_doc: {
    label: 'Approval Document',
    icon: 'i-lucide-file-check-2',
    tile: 'bg-yellow-50 dark:bg-yellow-500/10',
    badge:
      'bg-yellow-50 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
  },
  inventory: {
    label: 'Inventory Sheet',
    icon: 'i-lucide-table',
    tile: 'bg-n-alpha-2',
    badge: 'bg-n-alpha-2 text-n-slate-10',
    iconColor: 'text-n-slate-9',
  },
  other: {
    label: 'Other',
    icon: 'i-lucide-file',
    tile: 'bg-n-alpha-2',
    badge: 'bg-n-alpha-2 text-n-slate-10',
    iconColor: 'text-n-slate-9',
  },
};
function docMeta(type) {
  return DOC_TYPE_META[type] ?? DOC_TYPE_META.other;
}
function thumbnailFor(doc) {
  return (
    doc.thumbnail_url ||
    (doc.document_type === 'image' ? doc.shareable_url : null)
  );
}

const documents = ref([]);
const isLoading = ref(false);
const error = ref(null);
const searchTerm = ref('');
const typeFilter = ref('');
const sortBy = ref('newest'); // newest | title | type
const viewMode = ref('grid'); // grid | list
const activeFilter = ref('all'); // all | customer | internal
const openMenuId = ref(null);
const typeMenuOpen = ref(false);
const sortMenuOpen = ref(false);
const shareChecked = ref({});

const paperwork = computed(() =>
  documents.value.filter(d => !MEDIA_TYPES.includes(d.document_type))
);
const customerReady = computed(() =>
  paperwork.value.filter(d => d.customer_shareable)
);
const internalOnly = computed(() =>
  paperwork.value.filter(d => !d.customer_shareable)
);

const availableTypes = computed(() => [
  ...new Set(paperwork.value.map(d => d.document_type)),
]);

function matchesFilters(doc) {
  if (typeFilter.value && doc.document_type !== typeFilter.value) return false;
  if (searchTerm.value.trim()) {
    const q = searchTerm.value.trim().toLowerCase();
    if (!doc.title?.toLowerCase().includes(q)) return false;
  }
  return true;
}
function sortDocs(list) {
  const arr = [...list];
  if (sortBy.value === 'title')
    arr.sort((a, b) => a.title.localeCompare(b.title));
  else if (sortBy.value === 'type')
    arr.sort((a, b) =>
      docMeta(a.document_type).label.localeCompare(
        docMeta(b.document_type).label
      )
    );
  // 'newest' — API already returns created_at desc, keep as-is.
  return arr;
}

const filteredCustomerReady = computed(() =>
  sortDocs(customerReady.value.filter(matchesFilters))
);
const filteredInternal = computed(() =>
  sortDocs(internalOnly.value.filter(matchesFilters))
);

async function load() {
  isLoading.value = true;
  error.value = null;
  try {
    const { data } = await projectDocumentsAPI.get(props.projectId, {
      status: 'active',
    });
    documents.value = Array.isArray(data) ? data : [];
    shareChecked.value = Object.fromEntries(
      customerReady.value.map(d => [d.id, true])
    );
  } catch {
    error.value = 'Failed to load documents.';
  } finally {
    isLoading.value = false;
  }
}

async function copyLink(doc) {
  try {
    await navigator.clipboard.writeText(doc.shareable_url);
    useAlert('Link copied to clipboard.');
  } catch {
    useAlert('Could not copy link.');
  }
}

function toggleMenu(docId) {
  openMenuId.value = openMenuId.value === docId ? null : docId;
}
function closeMenus() {
  openMenuId.value = null;
  typeMenuOpen.value = false;
  sortMenuOpen.value = false;
}

const SORT_LABELS = {
  newest: 'Newest first',
  title: 'Title (A-Z)',
  type: 'Type',
};
function selectType(t) {
  typeFilter.value = t;
  typeMenuOpen.value = false;
}
function selectSort(s) {
  sortBy.value = s;
  sortMenuOpen.value = false;
}

// ── Share Project Kit panel ─────────────────────────────────────────────
const selectedShareDocs = computed(() =>
  customerReady.value.filter(d => shareChecked.value[d.id])
);
function shareMessageText() {
  const name = props.project?.name || 'this project';
  const lines = selectedShareDocs.value.map(
    d => `${d.title}: ${d.shareable_url}`
  );
  return [`${name} — documents:`, ...lines].join('\n');
}
// ── Recently Shared ──────────────────────────────────────────────────────
const recentShares = ref([]);
const CHANNEL_META = {
  whatsapp: { label: 'WhatsApp', icon: 'i-lucide-message-circle' },
  email: { label: 'Email', icon: 'i-lucide-mail' },
  copy_link: { label: 'Link copied', icon: 'i-lucide-copy' },
};
function shareTimeAgo(share) {
  return dynamicTime(Math.floor(new Date(share.created_at).getTime() / 1000));
}
async function loadRecentShares() {
  try {
    const { data } = await projectDocumentSharesAPI.get(props.projectId);
    recentShares.value = Array.isArray(data) ? data : [];
  } catch {
    // Non-fatal — the rest of the page still works without this.
  }
}

// Fire-and-forget: logs a real share event per selected document so
// "Recently Shared" reflects what actually happened, never fabricated.
// Never blocks the share action itself on this succeeding.
function logShares(channel) {
  Promise.all(
    selectedShareDocs.value.map(d =>
      projectDocumentSharesAPI.create(props.projectId, d.id, channel)
    )
  )
    .then(loadRecentShares)
    .catch(() => {
      // Non-fatal — the share itself already happened via wa.me/mailto/
      // clipboard; only the activity log entry failed to record.
    });
}
function shareViaWhatsApp() {
  if (!selectedShareDocs.value.length) {
    useAlert('Select at least one document to share.');
    return;
  }
  window.open(
    `https://wa.me/?text=${encodeURIComponent(shareMessageText())}`,
    '_blank',
    'noopener'
  );
  logShares('whatsapp');
}
function shareViaEmail() {
  if (!selectedShareDocs.value.length) {
    useAlert('Select at least one document to share.');
    return;
  }
  const subject = encodeURIComponent(
    `${props.project?.name || 'Project'} documents`
  );
  const body = encodeURIComponent(shareMessageText());
  window.location.href = `mailto:?subject=${subject}&body=${body}`;
  logShares('email');
}
async function copySelectedLinks() {
  if (!selectedShareDocs.value.length) {
    useAlert('Select at least one document to share.');
    return;
  }
  try {
    await navigator.clipboard.writeText(shareMessageText());
    useAlert('Links copied to clipboard.');
    logShares('copy_link');
  } catch {
    useAlert('Could not copy links.');
  }
}

onMounted(() => {
  load();
  loadRecentShares();
  document.addEventListener('click', closeMenus);
});
onUnmounted(() => {
  document.removeEventListener('click', closeMenus);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex-1 overflow-y-auto">
    <div v-if="isLoading" class="flex justify-center py-16">
      <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-7" />
    </div>

    <div
      v-else-if="error"
      class="m-6 text-sm text-red-600 dark:text-red-400 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30"
    >
      {{ error }}
    </div>

    <div
      v-else
      class="p-6 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start max-w-[1400px] mx-auto"
    >
      <!-- Main column -->
      <div class="flex flex-col gap-5 min-w-0">
        <!-- Header banner -->
        <div
          class="rounded-xl bg-gradient-to-br from-woot-50 to-woot-100/60 dark:from-woot-500/10 dark:to-woot-500/5 border border-woot-100 dark:border-woot-500/20 p-4 flex items-center justify-between gap-4 flex-wrap"
        >
          <div class="flex items-center gap-3">
            <span
              class="flex items-center justify-center size-10 rounded-xl bg-woot-500 text-white shrink-0"
            >
              <span class="i-lucide-file-stack size-5" />
            </span>
            <div>
              <h1 class="text-base font-semibold text-n-slate-12">
                Project Sales Kit
              </h1>
              <p class="text-xs text-n-slate-9 mt-0.5 max-w-md">
                Customer-ready documents to share with your prospects. AI
                Approved content ensures accurate and up-to-date information.
              </p>
            </div>
          </div>
          <button
            type="button"
            class="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 transition-colors shrink-0"
            @click="
              document
                .getElementById('share-kit-panel')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            "
          >
            <span class="i-lucide-send size-4" />
            Share Project Kit
          </button>
        </div>

        <!-- Toolbar -->
        <div class="flex items-center justify-between gap-3 flex-wrap">
          <div class="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              class="px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap"
              :class="
                activeFilter === 'all'
                  ? 'bg-woot-500 text-white'
                  : 'bg-n-alpha-2 text-n-slate-10 hover:bg-n-alpha-3'
              "
              @click="activeFilter = 'all'"
            >
              All ({{ paperwork.length }})
            </button>
            <button
              type="button"
              class="px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap"
              :class="
                activeFilter === 'customer'
                  ? 'bg-woot-500 text-white'
                  : 'bg-n-alpha-2 text-n-slate-10 hover:bg-n-alpha-3'
              "
              @click="activeFilter = 'customer'"
            >
              Customer Ready ({{ customerReady.length }})
            </button>
            <button
              type="button"
              class="px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap"
              :class="
                activeFilter === 'internal'
                  ? 'bg-woot-500 text-white'
                  : 'bg-n-alpha-2 text-n-slate-10 hover:bg-n-alpha-3'
              "
              @click="activeFilter = 'internal'"
            >
              Internal ({{ internalOnly.length }})
            </button>
          </div>

          <div class="flex items-center gap-2 flex-wrap">
            <div class="relative">
              <span
                class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-6 pointer-events-none"
              />
              <input
                v-model="searchTerm"
                type="text"
                placeholder="Search documents…"
                class="reset-base pl-8 pr-3 py-1.5 !mb-0 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-woot-500 w-48"
              />
            </div>

            <!-- Type dropdown -->
            <div class="relative">
              <button
                type="button"
                class="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-n-weak text-n-slate-10 hover:bg-n-alpha-2 transition-colors whitespace-nowrap"
                @click.stop="typeMenuOpen = !typeMenuOpen"
              >
                {{ typeFilter ? docMeta(typeFilter).label : 'Type' }}
                <span class="i-lucide-chevron-down size-3" />
              </button>
              <div
                v-if="typeMenuOpen"
                class="absolute right-0 top-full mt-1 z-10 w-44 max-h-64 overflow-y-auto rounded-lg border border-n-weak bg-n-background shadow-lg py-1"
                @click.stop
              >
                <button
                  type="button"
                  class="flex items-center w-full px-3 py-1.5 text-xs text-left hover:bg-n-alpha-2"
                  :class="
                    !typeFilter
                      ? 'text-woot-600 dark:text-woot-400 font-medium'
                      : 'text-n-slate-11'
                  "
                  @click="selectType('')"
                >
                  All types
                </button>
                <button
                  v-for="t in availableTypes"
                  :key="t"
                  type="button"
                  class="flex items-center w-full px-3 py-1.5 text-xs text-left hover:bg-n-alpha-2"
                  :class="
                    typeFilter === t
                      ? 'text-woot-600 dark:text-woot-400 font-medium'
                      : 'text-n-slate-11'
                  "
                  @click="selectType(t)"
                >
                  {{ docMeta(t).label }}
                </button>
              </div>
            </div>

            <!-- Sort dropdown -->
            <div class="relative">
              <button
                type="button"
                class="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-n-weak text-n-slate-10 hover:bg-n-alpha-2 transition-colors whitespace-nowrap"
                @click.stop="sortMenuOpen = !sortMenuOpen"
              >
                Sort: {{ SORT_LABELS[sortBy] }}
                <span class="i-lucide-chevron-down size-3" />
              </button>
              <div
                v-if="sortMenuOpen"
                class="absolute right-0 top-full mt-1 z-10 w-40 rounded-lg border border-n-weak bg-n-background shadow-lg py-1"
                @click.stop
              >
                <button
                  v-for="(label, key) in SORT_LABELS"
                  :key="key"
                  type="button"
                  class="flex items-center w-full px-3 py-1.5 text-xs text-left hover:bg-n-alpha-2"
                  :class="
                    sortBy === key
                      ? 'text-woot-600 dark:text-woot-400 font-medium'
                      : 'text-n-slate-11'
                  "
                  @click="selectSort(key)"
                >
                  {{ label }}
                </button>
              </div>
            </div>

            <div
              class="flex items-center rounded-lg border border-n-weak overflow-hidden shrink-0"
            >
              <button
                type="button"
                class="flex items-center justify-center size-8 transition-colors"
                :class="
                  viewMode === 'grid'
                    ? 'bg-woot-500 text-white'
                    : 'text-n-slate-8 hover:bg-n-alpha-2'
                "
                title="Grid view"
                @click="viewMode = 'grid'"
              >
                <span class="i-lucide-layout-grid size-3.5" />
              </button>
              <button
                type="button"
                class="flex items-center justify-center size-8 transition-colors border-l border-n-weak"
                :class="
                  viewMode === 'list'
                    ? 'bg-woot-500 text-white'
                    : 'text-n-slate-8 hover:bg-n-alpha-2'
                "
                title="List view"
                @click="viewMode = 'list'"
              >
                <span class="i-lucide-list size-3.5" />
              </button>
            </div>
          </div>
        </div>

        <!-- Customer Ready -->
        <div v-if="activeFilter !== 'internal'" class="flex flex-col gap-3">
          <div
            class="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-50 dark:bg-green-500/10"
          >
            <span
              class="flex items-center justify-center size-5 rounded-full bg-green-500 text-white shrink-0"
            >
              <span class="i-lucide-check size-3" />
            </span>
            <h2 class="text-sm font-semibold text-n-slate-12">
              Customer Ready
            </h2>
            <span class="ml-auto text-xs text-n-slate-8 shrink-0">
              {{ filteredCustomerReady.length }} asset{{
                filteredCustomerReady.length === 1 ? '' : 's'
              }}
            </span>
          </div>

          <p v-if="!customerReady.length" class="text-sm text-n-slate-8 px-1">
            No customer-shareable documents yet.
          </p>
          <p
            v-else-if="!filteredCustomerReady.length"
            class="text-sm text-n-slate-8 px-1"
          >
            No documents match your search.
          </p>

          <!-- Grid view -->
          <div
            v-else-if="viewMode === 'grid'"
            class="grid gap-3 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]"
          >
            <div
              v-for="doc in filteredCustomerReady"
              :key="doc.id"
              class="flex flex-col rounded-xl border border-n-weak overflow-hidden hover:shadow-md hover:border-woot-300 dark:hover:border-woot-700 transition-all"
            >
              <div class="relative h-36 shrink-0">
                <img
                  v-if="thumbnailFor(doc)"
                  :src="thumbnailFor(doc)"
                  :alt="doc.title"
                  class="w-full h-full object-cover"
                />
                <div
                  v-else
                  class="w-full h-full flex items-center justify-center"
                  :class="docMeta(doc.document_type).tile"
                >
                  <span
                    class="size-7"
                    :class="[
                      docMeta(doc.document_type).icon,
                      docMeta(doc.document_type).iconColor,
                    ]"
                  />
                </div>
                <span
                  class="absolute top-2 left-2 text-[11px] font-medium px-2 py-0.5 rounded-full shadow-sm"
                  :class="docMeta(doc.document_type).badge"
                >
                  {{ docMeta(doc.document_type).label }}
                </span>
              </div>
              <div class="flex flex-col gap-1.5 p-3 flex-1">
                <p
                  class="text-sm font-semibold text-n-slate-12 leading-snug line-clamp-1"
                >
                  {{ doc.title }}
                </p>
                <p
                  v-if="doc.document_file_name"
                  class="text-xs text-n-slate-7 truncate"
                >
                  {{ doc.document_file_name }}
                </p>
                <p
                  v-if="doc.approved_for_ai"
                  class="flex items-center gap-1 text-xs text-green-600 dark:text-green-400"
                >
                  <span class="i-lucide-check-circle-2 size-3" />
                  AI Approved
                </p>
                <p
                  class="flex items-center gap-1 text-xs text-green-600 dark:text-green-400"
                >
                  <span class="i-lucide-check-circle-2 size-3" />
                  Customer Shareable
                </p>
                <div class="flex items-center gap-1.5 mt-auto pt-1.5 relative">
                  <a
                    :href="doc.shareable_url || undefined"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-n-weak hover:bg-n-alpha-2 transition-colors"
                  >
                    <span class="i-lucide-eye size-3.5" />
                    Preview
                  </a>
                  <button
                    type="button"
                    class="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-woot-50 dark:bg-woot-500/10 text-woot-700 dark:text-woot-400 hover:bg-woot-100 dark:hover:bg-woot-500/20 transition-colors"
                    @click="copyLink(doc)"
                  >
                    <span class="i-lucide-share-2 size-3.5" />
                    Share
                  </button>
                  <button
                    type="button"
                    class="flex items-center justify-center size-7 rounded-lg border border-n-weak hover:bg-n-alpha-2 transition-colors shrink-0"
                    @click.stop="toggleMenu(doc.id)"
                  >
                    <span
                      class="i-lucide-more-vertical size-3.5 text-n-slate-8"
                    />
                  </button>
                  <div
                    v-if="openMenuId === doc.id"
                    class="absolute right-0 bottom-9 z-10 w-40 rounded-lg border border-n-weak bg-n-background shadow-lg py-1"
                    @click.stop
                  >
                    <a
                      :href="doc.shareable_url || undefined"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex items-center gap-2 px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-2"
                    >
                      <span class="i-lucide-external-link size-3.5" />
                      Open in New Tab
                    </a>
                    <button
                      type="button"
                      class="flex items-center gap-2 px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-2 w-full text-left"
                      @click="
                        copyLink(doc);
                        openMenuId = null;
                      "
                    >
                      <span class="i-lucide-link size-3.5" />
                      Copy Link
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- List view -->
          <div v-else class="flex flex-col gap-2">
            <div
              v-for="doc in filteredCustomerReady"
              :key="doc.id"
              class="flex items-center gap-3 p-2.5 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors"
            >
              <div class="relative size-11 rounded-lg overflow-hidden shrink-0">
                <img
                  v-if="thumbnailFor(doc)"
                  :src="thumbnailFor(doc)"
                  :alt="doc.title"
                  class="w-full h-full object-cover"
                />
                <div
                  v-else
                  class="w-full h-full flex items-center justify-center"
                  :class="docMeta(doc.document_type).tile"
                >
                  <span
                    class="size-4"
                    :class="[
                      docMeta(doc.document_type).icon,
                      docMeta(doc.document_type).iconColor,
                    ]"
                  />
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ doc.title }}
                </p>
                <p
                  class="text-xs text-n-slate-7 flex items-center gap-1 flex-wrap"
                >
                  <span
                    class="px-1.5 py-0.5 rounded-full"
                    :class="docMeta(doc.document_type).badge"
                  >
                    {{ docMeta(doc.document_type).label }}
                  </span>
                  <span
                    v-if="doc.approved_for_ai"
                    class="text-green-600 dark:text-green-400"
                  >
                    · AI Approved</span
                  >
                </p>
              </div>
              <div class="flex items-center gap-1.5 shrink-0">
                <a
                  :href="doc.shareable_url || undefined"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-xs font-medium px-2.5 py-1.5 rounded-lg border border-n-weak hover:bg-n-alpha-2 transition-colors"
                >
                  Preview
                </a>
                <button
                  type="button"
                  class="text-xs font-medium px-2.5 py-1.5 rounded-lg bg-woot-50 dark:bg-woot-500/10 text-woot-700 dark:text-woot-400 hover:bg-woot-100 dark:hover:bg-woot-500/20 transition-colors"
                  @click="copyLink(doc)"
                >
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Internal / Restricted -->
        <div v-if="activeFilter !== 'customer'" class="flex flex-col gap-3">
          <div
            class="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 dark:bg-red-500/10"
          >
            <span
              class="flex items-center justify-center size-5 rounded-full bg-red-500 text-white shrink-0"
            >
              <span class="i-lucide-lock size-3" />
            </span>
            <div class="min-w-0">
              <h2 class="text-sm font-semibold text-n-slate-12">
                Internal / Restricted
              </h2>
              <p class="text-[11px] text-n-slate-8">
                Internal documents are for team use only and not visible to
                customers.
              </p>
            </div>
            <span class="ml-auto text-xs text-n-slate-8 shrink-0">
              {{ filteredInternal.length }} asset{{
                filteredInternal.length === 1 ? '' : 's'
              }}
            </span>
          </div>

          <p v-if="!internalOnly.length" class="text-sm text-n-slate-8 px-1">
            No internal-only documents.
          </p>
          <p
            v-else-if="!filteredInternal.length"
            class="text-sm text-n-slate-8 px-1"
          >
            No documents match your search.
          </p>

          <div
            v-else
            class="grid gap-3 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]"
          >
            <div
              v-for="doc in filteredInternal"
              :key="doc.id"
              class="flex flex-col rounded-xl border border-n-weak bg-n-alpha-1 overflow-hidden opacity-80"
              title="Internal document — not shareable with customers"
            >
              <div class="relative h-36 shrink-0 grayscale">
                <img
                  v-if="thumbnailFor(doc)"
                  :src="thumbnailFor(doc)"
                  :alt="doc.title"
                  class="w-full h-full object-cover"
                />
                <div
                  v-else
                  class="w-full h-full flex items-center justify-center bg-n-alpha-2"
                >
                  <span class="i-lucide-lock size-7 text-n-slate-7" />
                </div>
              </div>
              <div class="flex flex-col gap-1.5 p-3 flex-1">
                <p
                  class="text-sm font-semibold text-n-slate-11 leading-snug line-clamp-1"
                >
                  {{ doc.title }}
                </p>
                <p class="text-xs text-n-slate-7">
                  {{ docMeta(doc.document_type).label }}
                </p>
                <p class="flex items-center gap-1 text-xs text-n-slate-8">
                  <span class="i-lucide-check-circle-2 size-3" />
                  Active
                </p>
                <p class="flex items-center gap-1 text-xs text-n-slate-8">
                  <span class="i-lucide-lock size-3" />
                  Internal Only
                </p>
                <div class="flex items-center gap-1.5 mt-auto pt-1.5">
                  <a
                    v-if="doc.shareable_url"
                    :href="doc.shareable_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-n-weak hover:bg-n-alpha-2 transition-colors"
                  >
                    <span class="i-lucide-eye size-3.5" />
                    View
                  </a>
                  <span
                    class="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-n-weak text-n-slate-6 cursor-not-allowed"
                  >
                    <span class="i-lucide-lock size-3.5" />
                    Share
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Share Project Kit sidebar -->
      <aside id="share-kit-panel" class="flex flex-col gap-3">
        <div class="rounded-xl border border-n-weak p-4 flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <span class="i-lucide-send size-4 text-woot-500 shrink-0" />
            <h2 class="text-sm font-semibold text-n-slate-12">
              Share {{ project?.name || 'Project' }}
            </h2>
          </div>
          <p class="text-xs text-n-slate-8">
            Select documents to include, then share with your prospect.
          </p>

          <div
            v-if="customerReady.length"
            class="flex flex-col gap-1.5 max-h-56 overflow-y-auto pr-1"
          >
            <label
              v-for="doc in customerReady"
              :key="doc.id"
              class="flex items-center gap-2 text-xs text-n-slate-11 cursor-pointer"
            >
              <input
                v-model="shareChecked[doc.id]"
                type="checkbox"
                class="rounded border-n-weak text-woot-500 focus:ring-woot-500"
              />
              <span class="truncate">{{ doc.title }}</span>
            </label>
            <p
              v-for="doc in internalOnly"
              :key="doc.id"
              class="flex items-center gap-2 text-xs text-n-slate-6"
            >
              <input type="checkbox" disabled class="rounded border-n-weak" />
              <span class="truncate">{{ doc.title }} (Internal)</span>
            </p>
          </div>
          <p v-else class="text-xs text-n-slate-7">
            No customer-ready documents to share yet.
          </p>

          <div class="flex flex-col gap-2 pt-1">
            <button
              type="button"
              class="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors"
              @click="shareViaWhatsApp"
            >
              <span class="i-lucide-message-circle size-4" />
              Share via WhatsApp
            </button>
            <button
              type="button"
              class="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-n-weak text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="shareViaEmail"
            >
              <span class="i-lucide-mail size-4" />
              Share via Email
            </button>
            <button
              type="button"
              class="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-n-weak text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="copySelectedLinks"
            >
              <span class="i-lucide-copy size-4" />
              Copy Links
            </button>
          </div>
          <p class="text-[11px] text-n-slate-6 leading-relaxed">
            Opens your own WhatsApp/email app to send — links point directly to
            each selected document.
          </p>
        </div>

        <!-- Recently Shared -->
        <div
          v-if="recentShares.length"
          class="rounded-xl border border-n-weak p-4 flex flex-col gap-3"
        >
          <h2 class="text-sm font-semibold text-n-slate-12">Recently Shared</h2>
          <div class="flex flex-col gap-3">
            <div
              v-for="share in recentShares"
              :key="share.id"
              class="flex items-start gap-2.5"
            >
              <span
                class="flex items-center justify-center size-7 rounded-full bg-n-alpha-2 shrink-0 mt-0.5"
              >
                <span
                  class="size-3.5 text-n-slate-9"
                  :class="
                    (CHANNEL_META[share.channel] || {}).icon ||
                    'i-lucide-share-2'
                  "
                />
              </span>
              <div class="min-w-0">
                <p class="text-xs font-medium text-n-slate-11 truncate">
                  {{ share.document_title }}
                </p>
                <p class="text-[11px] text-n-slate-7">
                  {{ share.shared_by_name || 'You' }} ·
                  {{
                    (CHANNEL_META[share.channel] || {}).label || share.channel
                  }}
                  · {{ shareTimeAgo(share) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>
